<script setup lang="ts">
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import ChangePasswordDialog from './ChangePasswordDialog.vue'
import { PRODUCT_NAME } from '../branding'
import { useAuth } from '../composables/useAuth'
import { openDialog } from '../ui/dialogs'
import { setMode as setEventLogMode, useEventLog } from '../ui/eventLog'
import { openMenu, type MenuItem } from '../ui/menus'

const props = defineProps<{
  /** Shown next to the product name, e.g. "Admin". */
  label?: string
  /** Whether to render the New / Open / Save controls. */
  showDocumentControls?: boolean
  documentName?: string
  busy?: boolean
  /**
   * Whether to surface the "Switch profile" menu item. Only true when the
   * caller (AppView) knows the user has 2+ capability profiles assigned —
   * anything less would offer a menu item that opens a one-option picker.
   */
  showSwitchProfile?: boolean
}>()

const emit = defineEmits<{
  new: []
  open: []
  save: []
  export: []
  switchProfile: []
}>()

const { email, isAdmin, logout } = useAuth()
const { mode: eventLogMode } = useEventLog()
const router = useRouter()
const route  = useRoute()

// Baked in at build time by vite.config.ts. Trim __BUILD_DATE__ to YYYY-MM-DD
// for display — the full ISO instant is more precision than the menu wants.
const appVersion    = __APP_VERSION__
const engineVersion = __ENGINE_VERSION__
const buildDate     = __BUILD_DATE__.slice(0, 10)

const accountBtn = ref<HTMLButtonElement | null>(null)

async function onSignOut() {
  await logout()
  await router.replace('/')
}

function onChangePassword() {
  void openDialog(ChangePasswordDialog, { mode: 'voluntary' })
}

function toggleEventLog() {
  // Menu is a full-visibility toggle: any on-state (collapsed or visible)
  // goes fully off; off goes to visible so the panel is immediately useful.
  setEventLogMode(eventLogMode.value === 'off' ? 'visible' : 'off')
}

function openAccountMenu() {
  if (!accountBtn.value) return
  const eventLogLabel = eventLogMode.value === 'off' ? 'Show event log' : 'Hide event log'
  // Build the items list conditionally — the framework does not have a
  // per-item `visible` prop; omit entries entirely instead. The event-log
  // toggle is only meaningful on /app (the admin view has no <cad-sketch>).
  const items: MenuItem[] = [
    isAdmin.value                 && { label: 'Admin',            action: () => router.push('/admin') },
    route.name === 'admin'        && { label: 'Back to app',      action: () => router.push('/app') },
    props.showSwitchProfile       && { label: 'Switch profile…',  action: () => emit('switchProfile') },
    route.name === 'app'          && { label: eventLogLabel,      action: toggleEventLog },
                                     { label: 'Change password',  action: onChangePassword },
                                     { label: 'Sign out',         action: onSignOut },
  ].filter(Boolean) as MenuItem[]

  openMenu(accountBtn.value, items, {
    footer: `App ${appVersion} · Engine ${engineVersion} · ${buildDate}`,
  })
}
</script>

<template>
  <header class="topbar">
    <div class="brand">
      <strong>{{ PRODUCT_NAME }}</strong>
      <span v-if="label" class="label">{{ label }}</span>
    </div>

    <div v-if="showDocumentControls" class="docs">
      <button type="button" :disabled="busy" @click="emit('new')">New</button>
      <button type="button" :disabled="busy" @click="emit('open')">Open</button>
      <button type="button" :disabled="busy" @click="emit('save')">Save</button>
      <button type="button" :disabled="busy" @click="emit('export')">Export</button>
      <span v-if="documentName" class="docname" :title="documentName">{{ documentName }}</span>
    </div>

    <div class="spacer" />

    <button
      ref="accountBtn"
      type="button"
      class="user-btn"
      aria-haspopup="menu"
      @click="openAccountMenu"
    >
      {{ email ?? 'Account' }}
      <span aria-hidden="true">▾</span>
    </button>
  </header>
</template>

<style scoped>
.topbar {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0 0.85rem;
  height: 3rem;
  flex: 0 0 auto;
  border-bottom: 1px solid var(--border);
  background: var(--bg);
}

.brand {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.brand strong {
  font-size: 1rem;
  letter-spacing: -0.01em;
}

.label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--fg-muted);
}

.docs {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.docs button {
  padding: 0.3rem 0.65rem;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--bg-subtle);
  color: var(--fg);
  font-size: 0.85rem;
}

.docs button:hover:not(:disabled) {
  border-color: var(--accent);
}

.docs button:disabled {
  opacity: 0.5;
  cursor: default;
}

.docname {
  margin-left: 0.4rem;
  font-size: 0.8rem;
  color: var(--fg-muted);
  max-width: 16rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.spacer { flex: 1 1 auto; }

/* Text-plus-chevron affordance for the account menu trigger. No border by
   default, hover reveals the border to signal interactivity. Unique to this
   spot in the top bar; not a candidate for a shared primitive yet. */
.user-btn {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.6rem;
  border: 1px solid transparent;
  border-radius: var(--radius);
  background: none;
  color: var(--fg);
  font-size: 0.85rem;
}

.user-btn:hover { border-color: var(--border); }
</style>
